/**
 * The only place in the front end that calls `fetch`.
 * Types come from `schema.d.ts`, generated from FastAPI's OpenAPI (`npm run gen:api`):
 * if the contract changes on the API side, the front end stops compiling.
 */
import type { components } from "./schema";

type S = components["schemas"];
export type Screening = S["ScreeningOut"];
export type HistoryItem = S["HistoryItemOut"];
export type SourceResult = S["SourceResultOut"];
export type Hit = S["HitOut"];
export type Source = S["SourceOut"];
export type SourceHealth = S["SourceHealthOut"];
export type SourceStatus = S["SourceStatus"];
export type User = S["UserOut"];
export type SignalState = S["SignalState"];
export type Decision = S["Decision"];
export type ScreeningInput = S["ScreeningIn"];
export type ReviewInput = S["ReviewIn"];

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

function messageFrom(status: number, body: unknown): string {
  const detail = (body as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return "Certains champs sont invalides.";
  if (status === 0) return "Serveur injoignable.";
  return `Erreur ${status}`;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method,
      credentials: "same-origin",
      headers: body === undefined ? {} : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, messageFrom(0, null));
  }
  if (response.status === 401 && path !== "/auth/login") {
    window.dispatchEvent(new CustomEvent("session-expired"));
  }
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new ApiError(response.status, messageFrom(response.status, data));
  }
  return (response.status === 204 ? undefined : await response.json()) as T;
}

export interface HistoryParams {
  q?: string;
  date?: string;
  state?: SignalState | "";
  scope?: "mine" | "all";
  limit?: number;
  offset?: number;
}

export function historyQuery(params: HistoryParams): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

export const api = {
  login: (token: string) => request<void>("POST", "/auth/login", { token }),
  logout: () => request<void>("POST", "/auth/logout"),
  me: () => request<User>("GET", "/auth/me"),
  sources: () => request<Source[]>("GET", "/sources"),
  createScreening: (input: ScreeningInput) => request<Screening>("POST", "/screenings", input),
  screening: (id: string) => request<Screening>("GET", `/screenings/${encodeURIComponent(id)}`),
  rerun: (id: string) => request<Screening>("POST", `/screenings/${encodeURIComponent(id)}/rerun`),
  review: (id: string, input: ReviewInput) =>
    request<Screening>("PATCH", `/screenings/${encodeURIComponent(id)}/review`, input),
  history: (params: HistoryParams = {}) =>
    request<HistoryItem[]>("GET", `/screenings${historyQuery(params)}`),
};

/** Download links: the browser sends the cookie itself, no fetch needed. */
export const downloads = {
  screeningCsv: (id: string) => `/api/screenings/${encodeURIComponent(id)}/export.csv`,
  screeningPdf: (id: string) => `/api/screenings/${encodeURIComponent(id)}/export.pdf`,
  historyCsv: (params: HistoryParams) => `/api/exports/history.csv${historyQuery(params)}`,
};
